import { useEffect, useRef, type ReactNode } from "react";

/** Closes when pointer/focus moves outside the container. */
export function useOutsideDismiss(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent | TouchEvent) {
      const el = ref.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
  return ref;
}

export type RoutingStep = {
  id: string;
  title: string;
  role: string;
  status: "completed" | "current" | "upcoming" | "rejected";
  at?: string;
  note?: string;
};

export function RequestRoutingPanel({
  title,
  steps,
  sla,
}: {
  title: string;
  steps: RoutingStep[];
  sla?: { stepTitle: string; minH: number; medianH: number; maxH: number } | null;
}) {
  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-navy">{title}</h3>
      {sla && (
        <div className="text-sm bg-mist border border-black/5 px-3 py-2 text-slate">
          Typical time for <strong className="text-navy">{sla.stepTitle}</strong> (last 60 days):{" "}
          <span className="text-navy">
            {sla.minH.toFixed(1)}–{sla.maxH.toFixed(1)} hrs
          </span>
          {" · "}
          median <strong className="text-navy">{sla.medianH.toFixed(1)} hrs</strong>
        </div>
      )}
      <ol className="space-y-3">
        {steps.map((s) => (
          <li key={s.id} className="flex gap-3">
            <span
              className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${
                s.status === "completed"
                  ? "bg-teal"
                  : s.status === "current"
                    ? "bg-gold ring-2 ring-gold/40"
                    : s.status === "rejected"
                      ? "bg-red-500"
                      : "bg-black/15"
              }`}
            />
            <div className="min-w-0">
              <div className="text-sm font-medium text-navy">{s.title}</div>
              <div className="text-xs text-slate">
                {s.role}
                {s.at ? ` · ${formatWhen(s.at)}` : ""}
              </div>
              {s.note && <p className="text-sm text-slate mt-1">{s.note}</p>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function PageHeader({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <div className="mb-4">
      <h1 className="font-display text-3xl text-navy">{title}</h1>
      {subtitle && <p className="text-slate mt-1 max-w-2xl">{subtitle}</p>}
    </div>
  );
}

function formatWhen(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export function median(nums: number[]): number {
  if (!nums.length) return 0;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

export function slaFor(samplesHours: number[]): { minH: number; medianH: number; maxH: number } {
  if (!samplesHours.length) return { minH: 0, medianH: 0, maxH: 0 };
  return {
    minH: Math.min(...samplesHours),
    medianH: median(samplesHours),
    maxH: Math.max(...samplesHours),
  };
}
